import { Logtail } from '@logtail/browser';

/**
 * Shared Better Stack (Logtail) browser client.
 *
 * Import this instead of constructing `new Logtail(...)` per module. Doing it
 * per module built one batcher, one `visibilitychange` listener and one
 * burst-protection counter each, 18 times over.
 *
 * Three behaviours of `@logtail/browser` 0.5.8 this encodes, all verified
 * against the installed `@logtail/core` source rather than assumed:
 *
 * 1. The constructor THROWS `"Logtail source token missing"` on an empty
 *    token, so an unset `NEXT_PUBLIC_LOGTAIL` would take down every module
 *    that imports this one. Without a token, hand back an inert client.
 * 2. A failed ingest request does NOT reject. `Base.log` catches it and, under
 *    the default `ignoreExceptions: false` / `throwExceptions: false`, calls
 *    `console.error(e)` itself: that is where `TypeError: Failed to fetch`
 *    comes from when an ad blocker or network policy blocks the endpoint, and
 *    no caller-side `.catch()` can suppress it. `ignoreExceptions: true` is
 *    the only switch that does. Drops are still counted in `logtail.dropped`.
 * 3. It is useless during server rendering, where there is no `document` for
 *    the SDK's flush-on-page-leave hook.
 */

type LogtailClient = Pick<Logtail, 'info' | 'error' | 'flush' | 'log'>;

const noop = async () => {};

const INERT = {
  info: noop,
  error: noop,
  log: noop,
  flush: noop,
} as LogtailClient;

const createLogtailClient = (): LogtailClient => {
  const sourceToken = process.env.NEXT_PUBLIC_LOGTAIL;
  if (typeof window === 'undefined' || !sourceToken) return INERT;

  // A logging outage must never surface as an application error.
  return new Logtail(sourceToken, { ignoreExceptions: true });
};

export const logtail = createLogtailClient();
